import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import SafeIcon from '../components/common/SafeIcon';
import ResourcePreview from '../components/resources/ResourcePreview';
import { useAuth } from '../context/AuthContext';
import {
  applyVersionToResource,
  checkCanVersionResource,
  fetchResourceById,
  fetchResourceVersion,
  fetchResourceVersionHistory,
  requestResourceVersionAccess,
} from '../services/resources';

export default function ResourceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canUploadResources } = useAuth();

  const [resource, setResource] = useState(null);
  const [displayResource, setDisplayResource] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versionLoading, setVersionLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [canVersion, setCanVersion] = useState(false);
  const [accessRequestState, setAccessRequestState] = useState({});
  const [showSuccess, setShowSuccess] = useState(
    Boolean(location.state?.uploadSuccess || location.state?.versionSuccess),
  );

  const selectedVersionId = searchParams.get('version');

  const loadVersionHistory = useCallback(async (resourceId) => {
    const history = await fetchResourceVersionHistory(resourceId);
    setVersionHistory(history);
    return history;
  }, []);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setNotFound(false);
      setAccessRequestState({});
      try {
        const row = await fetchResourceById(id);
        if (!active) return;
        if (!row) {
          setNotFound(true);
          setResource(null);
          setDisplayResource(null);
          setVersionHistory([]);
          setCanVersion(false);
          return;
        }
        setResource(row);
        setDisplayResource(row);
        await loadVersionHistory(id);
        if (!active) return;
        const allowed = await checkCanVersionResource(row.uploadedBy);
        if (active) setCanVersion(allowed);
      } catch (err) {
        console.error(err);
        if (active) {
          setNotFound(true);
          setResource(null);
          setDisplayResource(null);
          setCanVersion(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [id, loadVersionHistory]);

  useEffect(() => {
    if (!selectedVersionId || canUploadResources) return;
    setSearchParams({}, { replace: true });
  }, [selectedVersionId, canUploadResources, setSearchParams]);

  useEffect(() => {
    let active = true;

    async function loadSelectedVersion() {
      if (!resource || !selectedVersionId || !canUploadResources) {
        if (active) setDisplayResource(resource);
        return;
      }

      const currentEntry = versionHistory.find((v) => v.id === selectedVersionId);
      if (currentEntry?.isCurrent) {
        if (active) setDisplayResource(resource);
        return;
      }

      setVersionLoading(true);
      try {
        const versionRow = await fetchResourceVersion(id, selectedVersionId);
        if (!active) return;
        if (!versionRow) {
          setSearchParams({}, { replace: true });
          setDisplayResource(resource);
          return;
        }
        setDisplayResource(applyVersionToResource(resource, versionRow));
      } catch (err) {
        console.error(err);
        if (active) {
          setSearchParams({}, { replace: true });
          setDisplayResource(resource);
        }
      } finally {
        if (active) setVersionLoading(false);
      }
    }

    loadSelectedVersion();
    return () => { active = false; };
  }, [resource, selectedVersionId, canUploadResources, id, versionHistory, setSearchParams]);

  useEffect(() => {
    if (!location.state?.uploadSuccess && !location.state?.versionSuccess) return undefined;
    setShowSuccess(true);
    const timer = setTimeout(() => setShowSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [location.state?.uploadSuccess, location.state?.versionSuccess]);

  const handleViewVersion = (versionId, isCurrent) => {
    if (isCurrent) {
      setSearchParams({});
      return;
    }
    if (!canUploadResources) return;
    setSearchParams({ version: versionId });
  };

  const handleRequestAccess = async (versionId) => {
    setAccessRequestState((prev) => ({ ...prev, [versionId]: { loading: true, error: null } }));
    try {
      await requestResourceVersionAccess(id, versionId);
      setAccessRequestState((prev) => ({
        ...prev,
        [versionId]: { loading: false, sent: true, error: null },
      }));
    } catch (err) {
      setAccessRequestState((prev) => ({
        ...prev,
        [versionId]: { loading: false, sent: false, error: err.message || 'Error al enviar' },
      }));
    }
  };

  const successMessage = location.state?.versionSuccess
    ? (location.state?.versionMode === 'replace'
      ? 'Versión reemplazada correctamente.'
      : 'Nueva versión publicada correctamente.')
    : 'Recurso publicado correctamente.';

  const viewingArchived = Boolean(
    selectedVersionId
    && displayResource?.viewingVersionId
    && !versionHistory.find((v) => v.id === selectedVersionId)?.isCurrent,
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <p className="text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
          Cargando recurso…
        </p>
      </div>
    );
  }

  if (notFound || !resource || !displayResource) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-xl font-black text-[var(--text-main)] uppercase tracking-tight">
          Recurso no encontrado
        </p>
        <button
          type="button"
          onClick={() => navigate('/app/recursos')}
          className="gfi-gradient-bg text-brand-navy px-6 py-3 rounded-full font-black text-[10px] uppercase tracking-widest"
        >
          Volver al Hub
        </button>
      </div>
    );
  }

  const historyItems = versionHistory.length > 0 ? versionHistory : resource.versions;

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-20 transition-colors duration-300">
      {showSuccess && (
        <div className="sticky top-0 z-30 px-6 py-3 bg-brand-green/15 border-b border-brand-green/30 text-center">
          <p className="text-sm font-bold text-[var(--text-main)]">{successMessage}</p>
        </div>
      )}

      {viewingArchived && (
        <div className="sticky top-0 z-[25] px-6 py-3 bg-brand-cyan/10 border-b border-brand-cyan/30 flex flex-wrap items-center justify-center gap-3 text-center">
          <p className="text-sm font-bold text-[var(--text-main)]">
            Viendo versión archivada V{displayResource.viewingVersionLabel}
          </p>
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="text-[10px] font-black uppercase tracking-widest text-brand-navy bg-white/80 px-3 py-1.5 rounded-full border border-brand-cyan/40 hover:bg-white transition-colors"
          >
            Volver a versión actual
          </button>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-20 bg-[var(--bg-base)]/90 backdrop-blur-md p-4 flex items-center gap-4 border-b border-[var(--border-subtle)]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] hover:bg-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] transition-colors border border-[var(--border-subtle)]"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </button>
        <span className="font-bold text-[10px] uppercase tracking-[0.2em] text-[var(--text-secondary)] truncate flex-1">
          Volver al Hub
        </span>
        {canVersion && (
          <Link
            to={`/app/recursos/${id}/versionar`}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[10px] font-black uppercase tracking-widest text-[var(--text-main)] hover:border-brand-green transition-colors"
          >
            <SafeIcon name="RefreshCw" className="w-3.5 h-3.5" />
            Nueva versión
          </Link>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">

        {/* Left: Preview */}
        <div className="lg:col-span-7 relative">
          {versionLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--bg-base)]/60 rounded-3xl">
              <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                Cargando versión…
              </p>
            </div>
          )}
          <ResourcePreview resource={displayResource} />
        </div>

        {/* Right: Info & Actions */}
        <div className="lg:col-span-5 flex flex-col">
          <div className="flex gap-2 mb-6">
            <span className="px-3 py-1 gfi-gradient-bg text-brand-navy rounded-full text-[10px] font-black tracking-widest uppercase">
              {resource.category}
            </span>
            <span className="px-3 py-1 bg-[var(--bg-surface)] text-[var(--text-main)] rounded-full text-[10px] font-black tracking-widest uppercase border border-[var(--border-subtle)]">
              {resource.audience}
            </span>
            {viewingArchived && (
              <span className="px-3 py-1 bg-brand-cyan/15 text-brand-navy rounded-full text-[10px] font-black tracking-widest uppercase border border-brand-cyan/30">
                V{displayResource.viewingVersionLabel}
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-5xl font-black text-[var(--text-main)] mb-6 leading-[1.1] tracking-tighter uppercase italic">
            {resource.title}
          </h1>

          {resource.description ? (
            <p className="text-[var(--text-main)]/70 text-lg font-medium leading-relaxed mb-10">
              {resource.description}
            </p>
          ) : null}

          <div className="space-y-4 mb-12">
            <div className="flex items-center justify-between p-4 bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-subtle)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full gfi-gradient-bg p-[1px]">
                  <div className="w-full h-full rounded-full bg-[var(--bg-surface)] flex items-center justify-center text-xs font-black text-[var(--text-main)]">
                    {resource.uploader.charAt(0)}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-black text-[var(--text-main)] uppercase tracking-tight">Subido por</p>
                  <p className="text-sm font-bold text-[var(--text-secondary)]">{resource.uploader}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-[var(--text-main)] uppercase tracking-tight">
                  {viewingArchived ? 'Versión del' : 'Fecha'}
                </p>
                <p className="text-sm font-bold text-[var(--text-secondary)]">{displayResource.date}</p>
              </div>
            </div>
          </div>

          {/* Main Action */}
          {displayResource.link ? (
            <a
              href={displayResource.link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full gfi-gradient-bg text-brand-navy py-5 rounded-2xl font-black text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-4 transition-all transform hover:scale-[1.02] shadow-2xl mb-12"
            >
              <SafeIcon name={displayResource.type === 'file' ? 'Download' : 'ExternalLink'} className="w-5 h-5" />
              {displayResource.type === 'file'
                ? `Descargar ${displayResource.format}${viewingArchived ? ` (V${displayResource.viewingVersionLabel})` : ''}`
                : `Abrir en ${displayResource.source}`}
            </a>
          ) : null}

          {/* Version History */}
          {historyItems.length > 0 ? (
            <div className="mt-auto">
              <h3 className="text-xs font-black text-[var(--text-main)] uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
                <SafeIcon name="Clock" className="w-4 h-4 text-brand-green" /> Historial de Versiones
              </h3>
              <div className="space-y-3">
                {historyItems.map((v) => {
                  const isSelected = v.isCurrent
                    ? !selectedVersionId
                    : selectedVersionId === v.id;
                  const requestState = accessRequestState[v.id] ?? {};

                  return (
                    <div key={v.id ?? `${v.v}-${v.date}`} className="space-y-1">
                      <div
                        className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${
                          isSelected
                            ? 'border-brand-green/30 bg-brand-green/5'
                            : 'border-[var(--border-subtle)] bg-transparent'
                        } ${!v.isCurrent && canUploadResources ? 'opacity-100' : !v.isCurrent ? 'opacity-80' : ''}`}
                      >
                      <div className="font-black text-[10px] text-[var(--text-main)] mt-1 shrink-0">
                        V{v.v}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-[var(--text-main)]">{v.note}</p>
                        <p className="text-[9px] font-black text-[var(--text-secondary)] uppercase mt-1">{v.date}</p>
                        {v.isCurrent && (
                          <p className="text-[9px] font-black text-brand-green uppercase mt-1 tracking-widest">
                            Actual
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">
                        {v.isCurrent ? (
                          canUploadResources && selectedVersionId ? (
                            <button
                              type="button"
                              onClick={() => handleViewVersion(v.id, true)}
                              className="text-[9px] font-black uppercase tracking-widest text-brand-green hover:underline"
                            >
                              Ver
                            </button>
                          ) : null
                        ) : canUploadResources ? (
                          <button
                            type="button"
                            onClick={() => handleViewVersion(v.id, false)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[9px] font-black uppercase tracking-widest text-[var(--text-main)] hover:border-brand-green transition-colors"
                          >
                            <SafeIcon name="Eye" className="w-3 h-3" />
                            Ver
                          </button>
                        ) : requestState.sent ? (
                          <span className="text-[9px] font-black uppercase tracking-widest text-brand-green">
                            Enviado
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={requestState.loading}
                            onClick={() => handleRequestAccess(v.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-[9px] font-black uppercase tracking-widest text-brand-navy hover:bg-brand-cyan/20 transition-colors disabled:opacity-50"
                          >
                            {requestState.loading ? (
                              <SafeIcon name="Loader" className="w-3 h-3 animate-spin" />
                            ) : (
                              <SafeIcon name="Mail" className="w-3 h-3" />
                            )}
                            Solicitar acceso
                          </button>
                        )}
                      </div>
                      </div>
                      {requestState.error && (
                        <p className="text-[9px] text-red-400 px-4">{requestState.error}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
